/**
 * Contract form Zod schema — Phase 6a.8 (base) + Phase 6a.22 (modifications).
 *
 * The schema is the SINGLE source of truth for the contract entry form.
 * Phase 6a.8 shipped only the 3 always-open sections (Contract Info,
 * Payment Info, Discount Rate); Phase 6a.22 begins honouring the
 * deferred TODO by extending it with `modifications` (IFRS 16 §40-46).
 * Phases 6a.23–6a.26 will append multi-currency, termination, index
 * linkage, and prior-reconciliation in the same single-schema pattern.
 *
 * Error messages are i18n keys (resolved later via the form's FormMessage
 * component). z.coerce.number is used so native <input type="number">
 * string values are coerced before validation.
 */

import { z } from "zod";

import { getCurrentLocale } from "@/lib/format";
import { normalizeDecimalInput } from "@/lib/format/sanitizeFxRatePaste";
import { ASSET_CLASS_ORDER } from "@/types/engagement";

/**
 * Finding TR1 — locale-aware preprocessing for numeric fields fed by
 * free-text inputs (`<Input type="text" inputMode="decimal">`). Plain
 * `z.coerce.number()` runs `Number()` on the raw string, so TR grouping
 * "10.000" (= ten thousand) silently coerced to 10 — a 1000×
 * understatement with no validation error — and the TR decimal comma
 * "34,50" was rejected outright. Route the string through the same
 * locale-aware sanitizer the FX paste paths use before coercion.
 * Fields bound to native `<input type="number">` (payment.amount,
 * leaseTermMonths, discountRateAnnual, modifications…) keep plain
 * z.coerce — the browser constrains those to dot-decimal input.
 */
const localeDecimal = <T extends z.ZodTypeAny>(inner: T) =>
  z.preprocess(
    (v) =>
      typeof v === "string" ? normalizeDecimalInput(v, getCurrentLocale()) : v,
    inner,
  );

export const MODIFICATION_TYPES = [
  "INCREASE_SCOPE_SEPARATE",
  "INCREASE_SCOPE_NOT_SEPARATE",
  "DECREASE_SCOPE",
  "TERM_EXTENSION",
  "TERM_REDUCTION",
  "PAYMENT_CHANGE",
] as const;

/**
 * Phase 6a.22 — one modification entry per IFRS 16 §40-46.
 *
 * Backend (`ContractInput.modifications[]`) treats `newDiscountRate` as
 * required *except* when `modificationType === "INCREASE_SCOPE_SEPARATE"`
 * (the §44(a) "treat as new lease" carve-out — for that type, the entry
 * is an audit-trail breadcrumb only and the backend allows it without
 * a discount rate). The `.superRefine` below mirrors that conditional
 * requirement so RHF surfaces inline errors before the request leaves
 * the browser; everything below the surface is what the backend would
 * say anyway.
 *
 * Skipped fields in this phase (deferred per Phase 6a.22 scope decision):
 *   • `additionalPaymentStream` — rare advanced edge case (Phase 6a.22a)
 *   • `modificationDateSpotRate` — multi-currency, Phase 6a.23 territory
 */
export const modificationInputSchema = z
  .object({
    modificationId: z
      .string({ required_error: "error.required" })
      .min(1, "error.required")
      .max(100, "error.maxLength"),
    modificationDate: z
      .string({ required_error: "error.required" })
      .min(1, "error.required")
      .regex(/^\d{4}-\d{2}-\d{2}$/, "error.invalidDate"),
    modificationType: z.enum(MODIFICATION_TYPES, {
      required_error: "error.required",
      invalid_type_error: "error.required",
    }),
    newPaymentAmount: z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .positive("error.mustBePositive")
      .optional(),
    newRemainingTermMonths: z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .int("error.mustBeInteger")
      .min(1, "error.minLeaseTerm")
      .max(600, "error.maxLeaseTerm")
      .optional(),
    newDiscountRate: z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .positive("error.mustBePositive")
      .max(500, "error.maxDiscountRate")
      .optional(),
    partialTerminationPercent: z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .positive("error.mustBePositive")
      .max(99.99, "error.maxPartialTerminationPercent")
      .optional(),
    description: z.string().max(500, "error.maxLength").optional(),
  })
  .superRefine((mod, ctx) => {
    const needsTerm =
      mod.modificationType === "TERM_EXTENSION" ||
      mod.modificationType === "TERM_REDUCTION";
    const needsPayment =
      mod.modificationType === "PAYMENT_CHANGE" ||
      mod.modificationType === "INCREASE_SCOPE_NOT_SEPARATE";
    const needsPartial = mod.modificationType === "DECREASE_SCOPE";
    // §44(a) carve-out — newDiscountRate is NOT required for the
    // "separate contract" case; the entry is audit-trail only.
    const needsRate =
      mod.modificationType !== "INCREASE_SCOPE_SEPARATE";

    if (needsTerm && mod.newRemainingTermMonths == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["newRemainingTermMonths"],
        message: "error.required",
      });
    }
    if (needsPayment && mod.newPaymentAmount == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["newPaymentAmount"],
        message: "error.required",
      });
    }
    if (needsPartial && mod.partialTerminationPercent == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["partialTerminationPercent"],
        message: "error.required",
      });
    }
    if (needsRate && mod.newDiscountRate == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["newDiscountRate"],
        message: "error.required",
      });
    }
  });

export type ModificationFormValue = z.infer<typeof modificationInputSchema>;

// ─── FX / multi-currency (Phase 6a.23) ────────────────────────────────────

/**
 * Phase 6a.23 — single FX rate observation, one row in the historical /
 * payment-date rates tables.
 *
 * Phase 6a.23 addendum (2026-05-16) — the `rate` field uses
 * `z.coerce.number()` to match the backend's `FxRateEntry.rate: number`
 * contract. The `<Input type="text" inputMode="decimal">` rate cell
 * emits a string while the auditor is typing; the coercion converts
 * it once at safeParse time so the wire payload carries native
 * numbers (per Backend Spec §D.2.4 sealed at commit 86fbb51).
 *
 * Same class of fix as Phase 6a.11 Frontend Finding U (numeric
 * coercion for native `<input type="number">` fields).
 * `error.invalidPositiveDecimal` covers both the "not a number" and
 * "≤ 0" rejection paths — one message, two failure modes.
 *
 * `source` is intentionally NOT collected in the form. Every row
 * entered via the UI defaults to `"manual"` on the backend side
 * (the backend fills it when missing); auto-injection (Phase 6a.25
 * termination spot rates) is the only other producer and that runs
 * server-side.
 */
export const fxRateEntrySchema = z.object({
  date: z
    .string({ required_error: "error.required" })
    .min(1, "error.required")
    .regex(/^\d{4}-\d{2}-\d{2}$/, "error.invalidDate"),
  rate: localeDecimal(
    z.coerce
      .number({
        required_error: "error.invalidPositiveDecimal",
        invalid_type_error: "error.invalidPositiveDecimal",
      })
      .positive({ message: "error.invalidPositiveDecimal" }),
  ),
});

export type FxRateEntryFormValue = z.infer<typeof fxRateEntrySchema>;

// Monetary method only — SPOT_RATE_FIXED removed (not IAS 21-consistent).
export const FX_APPROACHES = ["IAS21_MONETARY"] as const;

// ─── Termination (Phase 6a.20c, IFRS 16 §46(a)) ──────────────────────────
//
// Mirrors backend `terminationInputSchema` (ifrs16-tool/backend/src/core/
// schemas.ts:135-145) 1-to-1. The backend's contract-level refines
// (terminationDate > commencementDate, period-end alignment, multi-
// currency rate requirements when an FX contract is terminated) stay on
// the backend; the form pre-validates only the bare-shape rules so the
// auditor sees inline issues for obvious mistakes (negative penalty,
// malformed date) without duplicating IFRS logic in two places.
export const terminationFormSchema = z.object({
  terminationDate: z
    .string({ required_error: "error.required" })
    .min(1, "error.required")
    .regex(/^\d{4}-\d{2}-\d{2}$/, "error.invalidDate"),
  terminationPenalty: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .nonnegative("error.mustBeNonNegative")
      .finite()
      .optional(),
  ),
  terminationPenaltyCurrency: z.enum(["contract", "functional"]).optional(),
  terminationPenaltyDateRate: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .positive("error.mustBePositive")
      .finite()
      .optional(),
  ),
  assetReturnDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "error.invalidDate")
    .optional(),
  restorationCostsActual: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .nonnegative("error.mustBeNonNegative")
      .finite()
      .optional(),
  ),
  terminationDateSpotRate: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .positive("error.mustBePositive")
      .finite()
      .optional(),
  ),
  description: z.string().max(1000, "error.maxLength").optional(),
});

export type TerminationFormValue = z.infer<typeof terminationFormSchema>;

// ─── Index linkage (Phase 6a.20d, IFRS 16 §42(b) / §43) ──────────────────
//
// Mirrors backend `indexResetEntrySchema` (ifrs16-tool/backend/src/core/
// schemas.ts:30-37). Negative `increaseRatePercent` down to -100 is
// permitted for contractual rent-reduction clauses (e.g. TÜFE
// deflation). Date alignment with a period-end is a SOFT hint at the
// component layer — backend's R2 refine accepts any date in the lease
// range and snaps to the next period-end internally.
export const indexResetEntryFormSchema = z.object({
  resetDate: z
    .string({ required_error: "error.required" })
    .min(1, "error.required")
    .regex(/^\d{4}-\d{2}-\d{2}$/, "error.invalidDate"),
  increaseRatePercent: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .finite()
      .gte(-100, "error.minIncreaseRate"),
  ),
  notes: z.string().max(1000, "error.maxLength").optional(),
});

export type IndexResetEntryFormValue = z.infer<
  typeof indexResetEntryFormSchema
>;

export const INDEX_TYPES = [
  "CPI_TUFE",
  "PPI_UFE",
  "D_PPI_YI_UFE",
  "CUSTOM",
] as const;

export const INDEX_RESET_FREQUENCIES = [
  "ANNUAL",
  "SEMI_ANNUAL",
  "CUSTOM",
] as const;

// ─── Prior year reconciliation (Phase 6a.20e, IFRS 16 §3.8/3.9 audit) ─────
//
// Mirrors the backend `priorPeriodBalances` sub-object (ifrs16-tool/backend/
// src/core/schemas.ts:245-258). All numeric leaves are coerced via the
// boundary helper in preparePayload — Zod-coerce here just so an inline
// "must be number" error fires while the auditor is still typing.
//
// The four core balances (rouGrossOpening, rouAccumulatedDepreciation,
// leaseLiabilityShortTerm, leaseLiabilityLongTerm) are required at the
// UI layer when the toggle is ON; the rest are optional. Backend's
// refines stay authoritative — the form is intentionally lenient on
// individual fields so the auditor can fill them in any order without
// being scolded mid-edit.
export const priorPeriodBalancesFormSchema = z.object({
  // PRIOR-RECON-CCY-BASIS — per-contract basis selector. Optional;
  // omitted/undefined → backend defaults to 'FUNCTIONAL'. See backend
  // `PriorPeriodBalances` in types.ts for the IAS 21.23 rate convention.
  currencyBasis: z.enum(["FUNCTIONAL", "CONTRACT"]).optional(),
  rouGrossOpening: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .nonnegative("error.mustBeNonNegative")
      .finite(),
  ),
  rouAccumulatedDepreciation: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .nonnegative("error.mustBeNonNegative")
      .finite(),
  ),
  leaseLiabilityShortTerm: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .nonnegative("error.mustBeNonNegative")
      .finite(),
  ),
  leaseLiabilityLongTerm: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .nonnegative("error.mustBeNonNegative")
      .finite(),
  ),
  // Optional balance items.
  restoration: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .nonnegative("error.mustBeNonNegative")
      .finite()
      .optional(),
  ),
  prepaid: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .nonnegative("error.mustBeNonNegative")
      .finite()
      .optional(),
  ),
  incentiveLiability: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .nonnegative("error.mustBeNonNegative")
      .finite()
      .optional(),
  ),
  // Multi-currency informational fields (no PAJE emitted from these —
  // backend uses them for the contract-ccy diagnostic line only).
  liabilityContractCcy: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .nonnegative("error.mustBeNonNegative")
      .finite()
      .optional(),
  ),
  priorYearClosingRate: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .positive("error.mustBePositive")
      .finite()
      .optional(),
  ),
});

export type PriorPeriodBalancesFormValue = z.infer<
  typeof priorPeriodBalancesFormSchema
>;

const paymentInputSchema = z.object({
  amount: z.coerce
    .number({
      required_error: "error.required",
      invalid_type_error: "error.notANumber",
    })
    .positive("error.mustBePositive"),
  frequency: z.enum(["MONTHLY", "QUARTERLY", "SEMI_ANNUAL", "ANNUAL"], {
    required_error: "error.required",
  }),
  timing: z.enum(["IN_ARREARS", "IN_ADVANCE"], {
    required_error: "error.required",
  }),
  firstPaymentDate: z
    .string({ required_error: "error.required" })
    .min(1, "error.required")
    .regex(/^\d{4}-\d{2}-\d{2}$/, "error.invalidDate"),
});

const contractFormShape = {
  // ── Contract Info section (always-open) ─────────────────────────────────
  contractId: z
    .string({ required_error: "error.required" })
    .min(1, "error.required")
    .max(100, "error.maxLength"),
  /**
   * Phase 6c.2 (Frontend Spec §21.7.1, Backend §20.3) — REQUIRED
   * 6-value asset class. No default; the form's Select primitive
   * renders a "Seçiniz..." placeholder and Zod rejects an empty
   * submission with `error.assetClassRequired`.
   */
  assetClass: z.enum(ASSET_CLASS_ORDER, {
    required_error: "error.assetClassRequired",
    invalid_type_error: "error.assetClassRequired",
  }),
  /**
   * Optional human-friendly contract label (Phase 6a.10 / Spec §21.7.7).
   * Used in engagement displays with fallback chain
   * `description || contractId || "—"`. Optional, max 200 chars; its
   * presence does NOT affect contract validation status.
   */
  description: z
    .string()
    .max(200, "error.descriptionTooLong")
    .optional(),
  commencementDate: z
    .string({ required_error: "error.required" })
    .min(1, "error.required")
    .regex(/^\d{4}-\d{2}-\d{2}$/, "error.invalidDate"),
  leaseTermMonths: z.coerce
    .number({
      required_error: "error.required",
      invalid_type_error: "error.notANumber",
    })
    .int("error.mustBeInteger")
    .min(1, "error.minLeaseTerm")
    .max(1200, "error.maxLeaseTerm"),
  currency: z
    .string({ required_error: "error.required" })
    .min(1, "error.required")
    .max(10, "error.maxLength"),

  // ── Payment Info section (always-open) ──────────────────────────────────
  payment: paymentInputSchema,

  // ── Discount Rate section (always-open) ─────────────────────────────────
  discountRateAnnual: z.coerce
    .number({
      required_error: "error.required",
      invalid_type_error: "error.notANumber",
    })
    .positive("error.mustBePositive")
    .max(500, "error.maxDiscountRate"),
  discountRateType: z.enum(["IBR", "IMPLICIT_RATE"], {
    required_error: "error.required",
  }),

  // ── Modifikasyonlar section (Phase 6a.22, IFRS 16 §40-46) ───────────────
  // Optional array. Empty array is valid; submit-time `preparePayload`
  // helper omits the field entirely from the backend payload when empty
  // (per Phase 6a.22 Spec §21.7.8.5 — never send `modifications: []`).
  modifications: z.array(modificationInputSchema).optional(),

  // ── FX / multi-currency section (Phase 6a.23, TMS 21 / IFRS 16) ─────────
  // All four fields are optional at the bare-shape level. The conditional
  // requirement ("commencementSpotRate required iff currency !==
  // functionalCurrency", "historical/payment rates required iff
  // IAS21_MONETARY FX contract") lives in the .superRefine() applied by
  // `buildContractFormSchema(functionalCurrency)` below — the form schema
  // can only judge those gates with the engagement's functional currency
  // in hand.
  //
  // Phase 6a.23 addendum — `commencementSpotRate` uses
  // `z.coerce.number()` (same rationale as `fxRateEntrySchema.rate`):
  // the rate input emits a string at the boundary, the backend's
  // `ContractInput.commencementSpotRate: number` is the wire contract.
  // Optional at this layer; the superRefine below upgrades the
  // requirement when the contract is an FX contract.
  commencementSpotRate: localeDecimal(
    z.coerce
      .number({
        invalid_type_error: "error.invalidPositiveDecimal",
      })
      .positive({ message: "error.invalidPositiveDecimal" })
      .optional(),
  ),
  // SPOT_RATE_FIXED removed — coerce a legacy stored value to IAS21_MONETARY so
  // editing an older FX contract validates cleanly under the monetary method.
  fxApproach: z
    .preprocess(
      (v) => (v === "SPOT_RATE_FIXED" ? "IAS21_MONETARY" : v),
      z.enum(FX_APPROACHES),
    )
    .optional(),
  historicalRates: z.array(fxRateEntrySchema).optional(),
  paymentDateRates: z.array(fxRateEntrySchema).optional(),

  // ── Termination section (Phase 6a.20c, IFRS 16 §46(a)) ─────────────────
  // Single termination per contract (NOT an array — terminal by definition).
  // Optional at the bare-shape level: when the toggle in
  // TerminationSection is OFF, this field is `undefined`. When ON, the
  // sub-schema enforces ISO date + nonneg numeric fields. The backend's
  // contract-level refines (date alignment, FX rate requirements) run
  // after submit — we don't duplicate them here.
  termination: terminationFormSchema.optional(),

  // ── Index linkage (Phase 6a.20d, IFRS 16 §42(b) / §43) ─────────────────
  // Top-level fields — mirrors backend `contractInputSchema` exactly.
  // `indexLinked` is the master toggle; the other fields stay undefined
  // until the auditor turns it on. The IndexLinkageSection component
  // reads/writes these via `useWatch` + `form.setValue` rather than
  // nesting them under an object. preparePayload strips the whole
  // family when `indexLinked === false` to keep the wire payload tidy.
  indexLinked: z.boolean().optional(),
  indexType: z.enum(INDEX_TYPES).optional(),
  indexResetFrequency: z.enum(INDEX_RESET_FREQUENCIES).optional(),
  indexCapPercent: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .positive("error.mustBePositive")
      .finite()
      .optional(),
  ),
  indexFloorPercent: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .finite()
      .optional(),
  ),
  indexResets: z.array(indexResetEntryFormSchema).optional(),

  // ── Prior year reconciliation (Phase 6a.20e, IFRS 16 §3.8/3.9) ─────────
  // Top-level fields — mirrors backend `contractInputSchema` exactly.
  // The PriorReconciliationSection's toggle is the source of truth: when
  // OFF, all three fields stay undefined; when ON, the section seeds
  // priorReportingDate="", priorPeriodBalances={}, priorReportingTolerance=0.01
  // and the auditor fills in the four core balances + any optional extras.
  // preparePayload strips the entire family when priorReportingDate is
  // empty / priorPeriodBalances is undefined.
  priorReportingDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "error.invalidDate")
    .optional(),
  priorPeriodBalances: priorPeriodBalancesFormSchema.optional(),
  priorReportingTolerance: z.coerce
    .number({ invalid_type_error: "error.notANumber" })
    .nonnegative("error.mustBeNonNegative")
    .finite()
    .optional(),

  // ── Additional Measurement Inputs (IFRS 16.27(c)-(d), 16.32) ────────────
  // Optional, additive A3/A4 fields. Bare-shape rules only (non-negative
  // amounts, positive integer useful life); the cross-field requirements
  // ("price required when option certain", "useful life required + >=
  // leaseTermMonths when ownership transfers") and the backend's
  // unsupported-combination guard (these fields cannot be combined with
  // FX / modifications / index-linkage / termination) live in the
  // .superRefine() applied by buildContractFormSchema below, because they
  // need the engagement functional currency and sibling fields in hand.
  residualValueGuarantee: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .nonnegative("error.mustBeNonNegative")
      .finite()
      .optional(),
  ),
  purchaseOptionPrice: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .nonnegative("error.mustBeNonNegative")
      .finite()
      .optional(),
  ),
  purchaseOptionReasonablyCertain: z.boolean().optional(),
  transfersOwnership: z.boolean().optional(),
  assetUsefulLifeMonths: localeDecimal(
    z.coerce
      .number({ invalid_type_error: "error.notANumber" })
      .int("error.mustBeInteger")
      .min(1, "error.mustBePositive")
      .max(1200, "error.maxLeaseTerm")
      .optional(),
  ),
} as const;

/**
 * Phase 6a.23 — schema factory. The conditional FX validation requires
 * the engagement's `functionalCurrency` to decide whether a given
 * contract is an FX contract. Since the schema can't read engagement
 * scope on its own, callers thread it in via this builder.
 *
 * The returned schema mirrors the historical `contractFormSchema` shape
 * exactly — only the conditional .superRefine() is added on top — so
 * existing typed values (`ContractFormValues`) and existing resolvers
 * stay compatible.
 */
export function buildContractFormSchema(opts: {
  functionalCurrency: string;
}) {
  const { functionalCurrency } = opts;
  return z.object(contractFormShape).superRefine((data, ctx) => {
    // ── Additional Measurement Inputs cross-field checks ─────────────────
    // Mirrors the backend `contractInputSchema` refines (IFRS 16.27(d),
    // 16.32) + the engine's UNSUPPORTED_MEASUREMENT_COMBINATION guard, so
    // the auditor sees inline issues before the request leaves the browser.
    // Runs regardless of FX status — keep it ABOVE the FX early-return.
    {
      // (a) purchaseOptionPrice required when the option is reasonably
      // certain to be exercised (IFRS 16.27(d)).
      if (
        data.purchaseOptionReasonablyCertain === true &&
        data.purchaseOptionPrice == null
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["purchaseOptionPrice"],
          message: "error.required",
        });
      }

      // (b) when ownership is expected to transfer (transfersOwnership or a
      // reasonably-certain purchase option), assetUsefulLifeMonths is
      // required and must be >= leaseTermMonths (IFRS 16.32).
      const ownershipTransfers =
        data.transfersOwnership === true ||
        data.purchaseOptionReasonablyCertain === true;
      if (
        ownershipTransfers &&
        (data.assetUsefulLifeMonths == null ||
          (typeof data.leaseTermMonths === "number" &&
            data.assetUsefulLifeMonths < data.leaseTermMonths))
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["assetUsefulLifeMonths"],
          message: "error.usefulLifeBelowLeaseTerm",
        });
      }

      // (c) surface the backend engine's UNSUPPORTED_MEASUREMENT_COMBINATION
      // guard inline: A3/A4 fields cannot be combined with FX
      // (currency !== functionalCurrency) / modifications / index-linkage /
      // termination. Raise on the first triggering measurement field so the
      // auditor fixes it before submit instead of hitting a 4xx.
      const measurementActive =
        (typeof data.residualValueGuarantee === "number" &&
          data.residualValueGuarantee > 0) ||
        (typeof data.purchaseOptionPrice === "number" &&
          data.purchaseOptionPrice > 0) ||
        data.transfersOwnership === true ||
        data.purchaseOptionReasonablyCertain === true;
      const ccyTrimmed = data.currency?.trim();
      const measurementIsFx =
        typeof ccyTrimmed === "string" &&
        ccyTrimmed.length > 0 &&
        ccyTrimmed !== functionalCurrency;
      const unsupportedContext =
        measurementIsFx ||
        (data.modifications?.length ?? 0) > 0 ||
        data.indexLinked === true ||
        data.termination != null;
      if (measurementActive && unsupportedContext) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["residualValueGuarantee"],
          message: "error.measurementUnsupportedCombo",
        });
      }
    }

    // FX contract test — only counts when both sides are well-formed
    // currency strings. An empty `currency` field is already caught by
    // the base-shape required check, so we just defensively bail here.
    const ccy = data.currency?.trim();
    if (!ccy) return;

    const isFxContract = ccy !== functionalCurrency;

    if (!isFxContract) {
      // MINI_FIX_BX — Non-FX contract: leftover FX fields (e.g. an
      // `fxApproach` that survived a functional-currency change, so the
      // contract's currency now EQUALS the functional currency) are
      // HARMLESS. The engine never reads FX fields on a non-FX contract,
      // so they cannot affect the calculation. The previous "fail loudly"
      // guard produced a FALSE POSITIVE: it blocked a valid
      // functional-currency change (e.g. TRY→USD functional turns the USD
      // contracts non-FX while their old `fxApproach` lingers) by marking
      // the slot `partial` and locking Calculate. We intentionally ignore
      // leftover FX fields here and return without raising an issue.
      return;
    }

    // FX contract — commencementSpotRate is REQUIRED.
    // Phase 6a.23 addendum: post-coercion this is `number | undefined`,
    // so a single `== null` test covers both the not-provided and the
    // leaf-rejected-and-stripped paths.
    if (data.commencementSpotRate == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["commencementSpotRate"],
        message: "error.required",
      });
    }

    // fxApproach defaults to IAS21_MONETARY when missing.
    const approach = data.fxApproach ?? "IAS21_MONETARY";

    if (approach === "IAS21_MONETARY") {
      // Phase 6a.20h — the schema validates a contract in isolation
      // (see file docstring). Backend `validateAllFXRates` (Phase 6c.1b)
      // decides whether the arrays are sufficient given the engagement
      // audit window — payments outside the window fall back to the
      // engagement closing rate. The schema-level "must be non-empty"
      // guards have been dropped to mirror that two-mode design.
      // Date-uniqueness checks survive: a duplicate date creates an
      // ambiguous re-translation basis regardless of audit-awareness.
      if (data.historicalRates) {
        const dates = new Set<string>();
        for (let i = 0; i < data.historicalRates.length; i++) {
          const row = data.historicalRates[i]!;
          if (dates.has(row.date)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["historicalRates", i, "date"],
              message: "error.duplicateDate",
            });
          }
          dates.add(row.date);
        }
      }
      if (data.paymentDateRates) {
        const dates = new Set<string>();
        for (let i = 0; i < data.paymentDateRates.length; i++) {
          const row = data.paymentDateRates[i]!;
          if (dates.has(row.date)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["paymentDateRates", i, "date"],
              message: "error.duplicateDate",
            });
          }
          dates.add(row.date);
        }
      }
    }
  });
}

/**
 * Default schema used by tests and any caller that doesn't know the
 * engagement's `functionalCurrency` upfront. Defaults to `"TRY"` —
 * matches the form's initial `currency: "TRY"` default, so a contract
 * left at default values is treated as non-FX and the FX rules stay
 * dormant. Production callers (ContractFormContainer + the persistence
 * pipeline) build the schema with the real engagement currency via
 * `buildContractFormSchema({ functionalCurrency })`.
 */
export const contractFormSchema = buildContractFormSchema({
  functionalCurrency: "TRY",
});

export type ContractFormValues = z.infer<typeof contractFormSchema>;
